import IsAxiosError from 'components/common/IsAxiosError';
import Loading from 'components/common/Loading';
import HubertCombobox from 'components/HubertUI/HubertCombobox';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useGetSupportedLanguagesQuery } from 'rtk/query/apis/misc';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';

interface Props extends ComponentProps<'div'> {
  isMulti?: boolean;
  value?: string | string[];
  excludeLanguages?: TAvailableLanguageCodes[];
  allowLanguages?: TAvailableLanguageCodes[];
  onValueChange?: (value: string | string[]) => void;
  disabled?: boolean;
  selectionSort?: boolean;
}

/**
 * This component refers to language codes in the form of "sv-se", "en-gb", "da-dk", etc. NOT
 * e.g. "sv", "en", etc. (i.e. WITH the region)
 * @param
 * @returns
 */
const LanguageCombobox = ({
  isMulti = false,
  onValueChange,
  value,
  excludeLanguages = [],
  allowLanguages = [],
  disabled = false,
  selectionSort = false,
  ...props
}: Props) => {
  const { data, isLoading, isFetching, isError, error } = useGetSupportedLanguagesQuery({
    exclude: excludeLanguages && excludeLanguages.length ? excludeLanguages : undefined,
    allow: allowLanguages && allowLanguages.length ? allowLanguages : undefined,
  });

  const [items, setItems] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (data?.languages) {
      const sortedItems = [...data.languages].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      );
      if (value && selectionSort) {
        const nonSelectedItems = sortedItems.filter((item) => !value.includes(item.value));
        const selectedItems = [...data.languages].filter((item) => value.includes(item.value));
        // Order of selectedItems is important as first item represent starting language
        setItems([...selectedItems, ...nonSelectedItems]);
      } else {
        setItems(sortedItems);
      }
    }
  }, [data?.languages, value, selectionSort]);

  if (isLoading || isFetching) {
    return <Loading label="languages" />;
  }

  if (isError) {
    return <IsAxiosError error={error} />;
  }

  if (!data) {
    return null;
  }

  return (
    <div {...props}>
      <HubertCombobox
        itemName="language/s"
        isMulti={isMulti}
        items={items}
        onValueChange={onValueChange}
        value={value}
        disabled={disabled}
        defaultSort={!selectionSort}
      />
    </div>
  );
};

export default LanguageCombobox;
